import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { cwPrettyDate, cwTodayISO, nightsBetween } from '../lib/format';
import { useProperty, resolveAsset } from '../context/PropertyContext';

/**
 * Print-friendly Arrivals sheet. Layout is minimal so paper is legible when the
 * internet is out entirely. Auto-triggers window.print() on mount.
 */
export default function PrintArrivals() {
  const [bookings, setBookings] = useState(null);
  const { property } = useProperty() || {};

  useEffect(() => {
    api.get('/bookings').then((r) => setBookings(r.data));
  }, []);

  useEffect(() => {
    if (bookings != null) {
      // give the browser a beat to paint before opening the print dialog
      const t = setTimeout(() => window.print(), 500);
      return () => clearTimeout(t);
    }
  }, [bookings]);

  if (!bookings) return <div className="p-8">Loading…</div>;

  const today = cwTodayISO();
  const rows = bookings
    .filter((b) => b.Status === 'Confirmed' && b.CheckIn === today && !b.CheckedInAt)
    .sort((a, b) => a.Name.localeCompare(b.Name));
  const alreadyIn = bookings.filter((b) => b.Status === 'Confirmed' && b.CheckIn === today && b.CheckedInAt);
  const now = new Date();

  return (
    <div className="print-sheet min-h-screen bg-white text-black p-8 sm:p-10">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
        }
        .print-sheet, .print-sheet * { font-family: 'DM Sans', system-ui, sans-serif; color: #111; }
        .print-sheet h1 { font-family: 'Fraunces', Georgia, serif; }
        .print-sheet table { width: 100%; border-collapse: collapse; }
        .print-sheet th, .print-sheet td { border-bottom: 1px solid #999; padding: 10px 8px; text-align: left; vertical-align: top; }
        .print-sheet th { border-bottom-width: 2px; font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; }
        .print-sheet .sig-line { border-bottom: 1px solid #333; height: 24px; }
      `}</style>

      <div className="no-print mb-4 flex items-center gap-3">
        <button
          onClick={() => window.print()}
          className="px-4 py-2 rounded-lg bg-[#2E7D32] text-white font-medium"
        >
          Print now
        </button>
        <button
          onClick={() => window.history.back()}
          className="px-4 py-2 rounded-lg border border-[#CFD8CF]"
        >
          Back
        </button>
      </div>

      <header className="flex items-center gap-4 border-b-2 border-black pb-4 mb-6">
        {property?.logo_url ? (
          <img alt="" src={resolveAsset(property.logo_url)} className="h-12 w-12 object-contain" />
        ) : (
          <div className="w-12 h-12 rounded-lg bg-[#2E7D32] text-white grid place-items-center font-bold" aria-hidden>B</div>
        )}
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest">{property?.name || 'Front Desk Console'}</div>
          <h1 className="text-3xl">Today's arrivals — {new Date(today).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</h1>
        </div>
        <div className="text-right text-xs">
          <div>Printed {now.toLocaleString('en-GB')}</div>
          {property?.reception_phone && <div>Reception {property.reception_phone}</div>}
        </div>
      </header>

      <section className="mb-6">
        <div className="mb-2 text-sm">{rows.length} guest{rows.length === 1 ? '' : 's'} expected</div>
        {rows.length === 0 ? (
          <div className="text-base">No arrivals today.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Guest</th>
                <th>Booking</th>
                <th>Room</th>
                <th>Guests · nights</th>
                <th>Checked in</th>
                <th>Signature</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => (
                <tr key={b['Booking#']}>
                  <td><strong>{b.Name}</strong><div className="text-xs">{b.Phone}</div></td>
                  <td className="font-mono text-xs">{b['Booking#']}</td>
                  <td>{b.RoomType}</td>
                  <td>{b.Guests} · {nightsBetween(b.CheckIn, b.CheckOut)}</td>
                  <td><div className="w-6 h-6 border border-black inline-block align-middle" aria-hidden /> □</td>
                  <td><div className="sig-line"/></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {alreadyIn.length > 0 && (
        <section>
          <h2 className="text-lg mb-2">Already arrived today</h2>
          <ul className="text-sm">
            {alreadyIn.map((b) => (
              <li key={b['Booking#']}>• {b.Name} — {b.RoomType} — checked in by {b.CheckedInBy || '—'}</li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
